/* eslint-disable consistent-return */
/* eslint-disable no-plusplus */
import { useState, useEffect } from 'react';

import { useMeetingViewStore } from '../../../store';

// Precompute a timeline mapping once when sections change
const getTimelineMapping = (sections) => {
  if (!sections) {
    return [];
  }

  const timeline = [];
  let startTime = 0;
  sections.forEach((section, index) => {
    // Save both the original index and the start time for this section
    timeline.push({ index, startTime, type: section.type });
    if (section.duration) {
      startTime += section.duration;
    }
  });

  // console.log('sections', sections);
  // console.log('timeline', timeline);

  return timeline;
};

// Select the first section with the same start time as the next section
const getNextSectionIndex = (timelineMapping, nextSectionIndexCandidate) => {
  const nextSectionCandidate = timelineMapping[nextSectionIndexCandidate];
  const sectionsWithTheSameStartTime = timelineMapping.filter(
    (section) => section.startTime === nextSectionCandidate.startTime
  );

  if (sectionsWithTheSameStartTime.length === 1) {
    return nextSectionIndexCandidate;
  }

  return sectionsWithTheSameStartTime?.[0]?.index;
};

const getNextSectionIndexCandidate = (timelineMapping, currentTime) => {
  // Find the section with the highest startTime less than or equal to currentTime.
  let low = 0;
  let high = timelineMapping.length - 1;
  let resultIndex = timelineMapping[0]?.index || 0;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (timelineMapping[mid].startTime <= currentTime) {
      resultIndex = timelineMapping[mid].index;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return resultIndex;
};

export default function useCurrentSectionIndex({ videoRef, sections }) {
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const timelineMapping = useMeetingViewStore((state) => state.timelineMapping);
  const setTimelineMapping = useMeetingViewStore(
    (state) => state.setTimelineMapping
  );

  // Precompute the timeline mapping when the sections change
  useEffect(() => {
    setTimelineMapping(getTimelineMapping(sections));
  }, [sections, setTimelineMapping]);

  // Check for the next section after video ends
  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const handleEnded = () => {
      setCurrentSectionIndex((currentIndex) => {
        // Find the position of the currentIndex in the timeline array
        const pos = timelineMapping.findIndex(
          (item) => item.index === currentIndex
        );

        // If there’s a “next” entry in the timeline, use its index,
        // otherwise stick with the currentIndex
        if (pos >= 0 && pos + 1 < timelineMapping.length) {
          return timelineMapping[pos + 1].index;
        }
        return currentIndex;
      });
    };

    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('ended', handleEnded);
    };
  }, [timelineMapping, videoRef]);

  useEffect(() => {
    const watchVideoTime = () => {
      // Don’t do anything if video is not playing
      if (
        !videoRef.current ||
        videoRef.current.paused ||
        videoRef.current.ended
      ) {
        return;
      }

      const currentTime = videoRef.current?.currentTime || 0;
      const nextSectionIndexCandidate = getNextSectionIndexCandidate(
        timelineMapping,
        currentTime
      );

      const { minSectionIndex } = useMeetingViewStore.getState();
      const newSectionIndex = getNextSectionIndex(
        timelineMapping,
        nextSectionIndexCandidate
      );

      if (minSectionIndex > newSectionIndex) {
        setCurrentSectionIndex(nextSectionIndexCandidate);
      } else {
        setCurrentSectionIndex(newSectionIndex);
      }

      if (!videoRef.current?.ended && !videoRef.current?.paused) {
        requestAnimationFrame(watchVideoTime);
      }
    };

    const videoElement = videoRef.current;

    if (videoElement) {
      videoElement.addEventListener('play', watchVideoTime);
    }

    return () => {
      if (videoElement) {
        videoElement.removeEventListener('play', watchVideoTime);
      }
    };
  }, [timelineMapping, videoRef]);

  return {
    currentSectionIndex,
    setCurrentSectionIndex,
    getNextSectionIndexCandidate,
  };
}
